import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { IFlightPathSnapshot, IMapSnapshot, IRouteHistory } from 'src/app/models';
import { GeoJsonObject } from 'src/app/models/geoJsonObject';

const DB_NAME = 'map-history-db';
const DB_VERSION = 1;
const STORE_NAME = 'route-snapshots';

@Injectable({
  providedIn: 'root',
})
export class MapHistoryService {
  public maxSnapshots = 500;  // 0 for unlimited

  public canUndo$ = new BehaviorSubject<boolean>(false);
  public canRedo$ = new BehaviorSubject<boolean>(false);

  private db: IDBDatabase | null = null;

  // ─── DB lifecycle ───────────────────────────────────────────────────────────

  private openDb(): Promise<IDBDatabase> {
    if (this.db) return Promise.resolve(this.db);

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'routeId' });
        }
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        resolve(this.db);
      };

      request.onerror = (event) => {
        reject((event.target as IDBOpenDBRequest).error);
      };
    });
  }

  private async getEntry(routeId: number): Promise<IRouteHistory | undefined> {
    const db = await this.openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const request = tx.objectStore(STORE_NAME).get(routeId);
      request.onsuccess = () => resolve(request.result as IRouteHistory | undefined);
      request.onerror = () => reject(request.error);
    });
  }

  private async putEntry(entry: IRouteHistory): Promise<void> {
    const db = await this.openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const request = tx.objectStore(STORE_NAME).put(entry);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private async deleteEntry(routeId: number): Promise<void> {
    const db = await this.openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const request = tx.objectStore(STORE_NAME).delete(routeId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ─── Public API ─────────────────────────────────────────────────────────────

  async initHistory(routeId: number): Promise<void> {
    await this.deleteEntry(routeId).catch(() => {});
    this.updateSubjects(false, false);
  }

  async pushSnapshot(routeId: number, features: GeoJsonObject, flightPath?: IFlightPathSnapshot, editingWaypointsSegmentId?: number | null): Promise<void> {
    let entry = await this.getEntry(routeId);

    if (!entry) {
      entry = { routeId, snapshots: [], currentIndex: -1 };
    }

    entry.snapshots = entry.snapshots.slice(0, entry.currentIndex + 1);

    const snapshot: IMapSnapshot = {
      timestamp: Date.now(),
      features: JSON.parse(JSON.stringify(features)),
      ...(flightPath ? { flightPath: JSON.parse(JSON.stringify(flightPath)) } : {}),
      ...(editingWaypointsSegmentId != null ? { editingWaypointsSegmentId } : {})
    };
    entry.snapshots.push(snapshot);
    entry.currentIndex = entry.snapshots.length - 1;

    if (this.maxSnapshots > 0 && entry.snapshots.length > this.maxSnapshots) {
      const overflow = entry.snapshots.length - this.maxSnapshots;
      entry.snapshots.splice(0, overflow);
      entry.currentIndex = entry.snapshots.length - 1;
    }

    await this.putEntry(entry);
    this.updateSubjects(entry.currentIndex > 0, false);
  }

  async undo(routeId: number): Promise<IMapSnapshot | null> {
    const entry = await this.getEntry(routeId);
    if (!entry || entry.currentIndex <= 0) return null;

    entry.currentIndex -= 1;
    await this.putEntry(entry);

    const snapshot = entry.snapshots[entry.currentIndex];
    this.updateSubjects(entry.currentIndex > 0, true);
    return snapshot;
  }

  async redo(routeId: number): Promise<IMapSnapshot | null> {
    const entry = await this.getEntry(routeId);
    if (!entry || entry.currentIndex >= entry.snapshots.length - 1) return null;

    entry.currentIndex += 1;
    await this.putEntry(entry);

    const snapshot = entry.snapshots[entry.currentIndex];
    this.updateSubjects(entry.currentIndex > 0, entry.currentIndex < entry.snapshots.length - 1);
    return snapshot;
  }

  async clearHistory(routeId: number): Promise<void> {
    await this.deleteEntry(routeId).catch(() => {});
    this.updateSubjects(false, false);
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────

  private updateSubjects(canUndo: boolean, canRedo: boolean): void {
    this.canUndo$.next(canUndo);
    this.canRedo$.next(canRedo);
  }
}
